
// * ========================================================================
// * Summary chart
// * ========================================================================

import CovidChart from './covid-chart.js';
import moment from 'moment';
import storage from './../storage';

const hospitalarea = (function() {
    const typeSelectorClass = 'input[name=hospital-by-area-chart-data]';
    let thisChart;
    let chartData = null;
    let dataType = null;
    let chartFirstDate = null;
    let typeSelectors = null;

    /**
     *
     * Create new chart
     *
     * @param {Object} data - covid data
     * @param {Object} options - chart options
     * @param {Array} selected - selected options
     *
     * @returns {Object} - chart
     */
    function createChartData(data, options, selected) {
        const datasets = [{ // Guide for LineWithLine
            label: '',
            id: 'hidden-guide',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0,0,0,0)',
            data: [],
            type: 'LineWithLine',
            selected: true,
            order: 0,
            pointRadius: 0
        }];

        const areaIdxMap = {};

        for (let day of data.hospital.areas) {
            const newDate = moment(day.date);
            newDate.set({
                hour: 0,
                minute: 0,
                second: 0
            });

            if (newDate >= chartFirstDate) {
                datasets[0].data.push({
                    x: newDate,
                    y: 0
                });

                for (const area of day.areas) {
                    if (area.id === 'all') {
                        continue;
                    }

                    if (typeof areaIdxMap[area.id] === 'undefined') {
                        areaIdxMap[area.id] = datasets.length;
                        datasets.push({
                            label: area.name,
                            id: area.id,
                            backgroundColor: options.chartColors[areaIdxMap[area.id]],
                            borderColor: options.chartColors[areaIdxMap[area.id]],
                            labelColor: options.chartColors[areaIdxMap[area.id]],
                            data: [],
                            type: 'LineWithLine',
                            selected: selected && Array.isArray(selected) ? selected.indexOf(area.id) !== -1 : true,
                            pointRadius: 0,
                            tension: 0.2
                        });
                    }

                    let count;
                    if (dataType === 'due') {
                        count = area.forCovidPrimary + area.forCovidSpecial;
                    } else if (dataType === 'with') {
                        count = area.withCovidPrimary + area.withCovidSpecial;
                    } else {
                        count = area.hospitalized;
                    }

                    datasets[areaIdxMap[area.id]].data.push({
                        x: newDate,
                        y: count
                    });

                }
            }
        }

        // Dotted
        const lag = 4; // weeks
        const dataLagIdx = datasets[0].data.length - lag;
        const dashed = function (ctx, value) {
            if (ctx.p0DataIndex > dataLagIdx) {
                return value;
            }
            return undefined;
        }

        for (let dataset of datasets) {
            dataset.segment = {
                borderDash: ctx => dashed(ctx, [6, 6]),
            };
        }

        return datasets;
    }

    function getDataType(initialType = null) {
        let initialCheckbox = null;
        if (initialType) {
            initialCheckbox = document.querySelector(typeSelectorClass + '[value="'+initialType+'"]');
        }

        if (initialCheckbox) {
            initialCheckbox.checked = true;
            dataType = initialType;
            return dataType;
        }

        dataType = document.querySelector(typeSelectorClass+':checked').value;

        return dataType;
    }

    /**
     * Initialize
     *
     * @param {Object} data - covid data
     */
    function init(data) {
        chartData = data;
        chartFirstDate = window.config.firstDayValues['hospital'];

        typeSelectors = document.querySelectorAll(typeSelectorClass);

        const initialType = storage.getData(typeSelectorClass);

        // Get the data type we want to show
        getDataType(initialType);

        // Initially selected
        let selectedareas = storage.getData('hospital-by-area-chart_multiselect');
        if (!selectedareas || !Array.isArray(selectedareas)) {
            selectedareas = null;
        }

        const datasets = createChartData(chartData, window.config, selectedareas);

        thisChart = new CovidChart('hospital-by-area-chart', {
            datasets,
            chartOptions: {
                options: {
                    legend: {
                        display: true,
                        labels: {
                            boxWidth: 12
                        }
                    },
                    plugins: {
                        tooltip: {
                            position: 'nearest',
                            intersect: false,
                            bodySpacing: 5,
                            itemSort: function(a, b, data) {
                                return b.parsed.y > a.parsed.y ? 1 : -1;
                            },
                        }
                    },
                    scales: {
                        x: {
                            type: 'time',
                            display: true,
                            time: {
                                unit: 'day',
                                displayFormats: {
                                    day: 'D.M.YY',
                                }
                            },
                            ticks: {
                                major: {
                                    fontStyle: 'bold',
                                    fontColor: '#FF0000'
                                }
                            }
                        },
                        y: {
                            stacked: false,
                            min: 0,
                            type: 'linear',
                            position: 'left',
                        }
                    }
                }
            },
            delayAnnotation: true,
            config: window.config,
            tooltips: {
                elementId: 'hospital-by-area-chart-tooltip'
            },
            multiselect: {
                requireCount: false,
                valueType: 'string',
                idField: 'id',
                colorField: 'labelColor',
                inputName: 'hospital-by-area-multiselect',
                selectElemId: 'hospital-by-area-multiselect',
                colorElemId: 'hospital-by-area-multiselect-colors',
                dropdownId: 'hospital-by-area-multiselect-dropdown',
                selectAllElemId: 'hospital-by-area-multiselect-select-all',
                clearAllElemId: 'hospital-by-area-multiselect-clear-all',
                countElemId: 'hospital-by-area-multiselect-count'
            }
        });

        // Create listener for type change
        typeSelectors.forEach((typeSelector) => {
            typeSelector.addEventListener('change', () => {
                let newType = document.querySelector(typeSelectorClass+':checked').value;
                storage.updateData(typeSelectorClass, newType);
                if (dataType !== newType) {
                    reload(null, true);
                }
            });
        });
    }

    function reload(data = null, changeType = false) {
        if (data) {
            chartData = data;
        }

        // Get the data type we want to show
        getDataType();

        if (!changeType) {
            // New first date value
            chartFirstDate = window.config.firstDayValues['hospital'];
        }

        const datasets = createChartData(chartData, window.config);
        thisChart.updateDatasets(datasets);
    }

    return {
        init,
        reload
    }

})();

export default hospitalarea;
